<template>
    <div class="mapping_container">
        <v-table class="mapping_table">
            <thead>
                <tr>
                    <th class="text-left">Campo esperado</th>
                    <th class="text-left">Columna de Excel</th>
                    <th class="text-left">Estado</th>
                    <th class="text-left">Muestra</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="field in mapping" :key="field.key">
                    <td>
                        <div class="field_identity">
                            <div class="field_name">
                                <span class="field_label">{{ field.label }}</span>
                                <span v-if="field.required" class="text-red ml-1">*</span>
                            </div>
                            <div class="field_source">XLSX: {{ field.key }} · Columna {{ excelColumnLabel(field.expectedIndex) }}</div>
                        </div>
                    </td>
                    <td>
                        <v-select
                            :model-value="field.providedIndex"
                            :items="selectItems(field)"
                            :item-props="itemProps"
                            density="compact"
                            variant="outlined"
                            hide-details
                            clearable
                            :disabled="providedColumns.length === 0"
                            @update:model-value="value => onSelect(field, value)"
                        ></v-select>
                    </td>
                    <td>
                        <v-chip :color="statusColor(field.status)" size="small" variant="flat">
                            {{ statusLabel(field.status) }}
                        </v-chip>
                    </td>
                    <td class="text-caption text-grey-darken-1 sample_cell">
                        <v-tooltip v-if="sampleFor(field) !== ''" :text="String(sampleFor(field))" location="top">
                            <template v-slot:activator="{ props }">
                                <span v-bind="props" class="sample_value">{{ sampleFor(field) }}</span>
                            </template>
                        </v-tooltip>
                    </td>
                </tr>
            </tbody>
        </v-table>
    </div>
</template>

<script>
    import { excelColumnLabel } from '@/utils/excelColumn';

    export default {
        name: 'ImportColumnMapping',
        props: {
            mapping: {
                type: Array,
                default: () => [],
            },
            allMapping: {
                type: Array,
                default: () => [],
            },
            providedColumns: {
                type: Array,
                default: () => [],
            },
            sampleRow: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            usedIndexes () {
                const used = new Set();
                const fields = this.allMapping.length ? this.allMapping : this.mapping;
                fields.forEach((field) => {
                    if (field.providedIndex !== null && field.providedIndex !== undefined) {
                        used.add(field.providedIndex);
                    }
                });
                return used;
            }
        },
        methods: {
            excelColumnLabel,
            selectItems (field) {
                return this.providedColumns.map((column) => {
                    const isUsedByOther = this.usedIndexes.has(column.index)
                        && field.providedIndex !== column.index;
                    return {
                        value: column.index,
                        title: this.columnTitle(column),
                        disabled: isUsedByOther,
                    };
                });
            },
            itemProps (item) {
                return { disabled: item.disabled };
            },
            columnTitle (column) {
                const letter = excelColumnLabel(column.index);
                const name = column.name !== null && column.name !== undefined && column.name !== ''
                    ? column.name
                    : null;
                return name ? `${name} (${letter})` : `Columna ${letter}`;
            },
            onSelect (field, value) {
                this.$emit('update', {
                    key: field.key,
                    providedIndex: value === null || value === undefined ? null : value,
                });
            },
            sampleFor (field) {
                if (field.providedIndex === null || field.providedIndex === undefined) {
                    return '';
                }
                const value = this.sampleRow[field.providedIndex];
                return value === null || value === undefined || value === '' ? '-' : value;
            },
            statusLabel (status) {
                const labels = {
                    matched: 'Coincide',
                    mismatched: 'Revisar',
                    missing: 'Faltante',
                    optional: 'Opcional',
                    ignored: 'Ignorada',
                };
                return labels[status] || status;
            },
            statusColor (status) {
                const colors = {
                    matched: 'green',
                    mismatched: 'orange',
                    missing: 'red',
                    optional: 'grey',
                    ignored: 'grey-lighten-1',
                };
                return colors[status] || 'grey';
            },
        },
    };
</script>

<style scoped>
    .mapping_container {
        display: flex;
        flex: 1;
        min-height: 0;
        min-width: 0;
    }

    .mapping_table {
        flex: 1;
        min-height: 0;
        min-width: 0;
    }

    .mapping_table :deep(.v-table__wrapper) {
        flex: 1;
        min-height: 0;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .mapping_table :deep(table) {
        display: flex;
        flex-direction: column;
        min-width: 810px;
        height: 100%;
    }

    .mapping_table :deep(thead) {
        display: block;
        flex: none;
        overflow-y: auto;
        scrollbar-gutter: stable;
    }

    .mapping_table :deep(tbody) {
        display: block;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        scrollbar-gutter: stable;
    }

    .mapping_table :deep(tr) {
        display: grid;
        grid-template-columns: minmax(200px, 1fr) minmax(300px, 1.5fr) 130px minmax(180px, 1fr);
        align-items: center;
    }

    .mapping_table :deep(th),
    .mapping_table :deep(td) {
        min-width: 0;
    }

    .mapping_table :deep(tbody tr) {
        min-height: 72px;
    }

    .mapping_table :deep(tbody td) {
        display: flex;
        align-items: center;
        padding: 10px 16px;
    }

    .field_identity {
        display: flex;
        flex-direction: column;
        gap: 5px;
        min-width: 0;
        padding: 3px 0;
    }

    .field_name {
        display: flex;
        align-items: baseline;
        min-width: 0;
    }

    .field_label {
        font-weight: 600;
        font-size: 14px;
        line-height: 1.3;
        overflow-wrap: anywhere;
    }

    .field_source {
        color: #646b73;
        font-size: 12px;
        line-height: 1.4;
        overflow-wrap: anywhere;
    }

    .sample_value {
        display: block;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        opacity: 0.8;
    }
</style>
